import WebPageLayout from "../../../../layouts/WebPageLayout";
import { Card, Row, Col, Button, Statistic } from "antd";
import React, { useState } from "react";
import {GetWorkOrderScreenHeader} from "../work-order-screen-headers";
import {WorkOrdersStackParamList} from "../../WorkOrdersStack";
import {ScreenProps} from "../../../../../../utils/screen-props-helper";
import {
  useGetWorkOrderQuery,
  useLazyGetWorkOrderAssetsQuery
} from "../../../../../../store/services/api/work-order-api";
import {WorkOrderAsset} from "../../../../../../store/data/entities/work-order-asset";
import useEntityListPage from "../../../../hooks/useEntityListPage";
import {CoreTable} from "../../../../components/CoreTable";
import {DataTable, Tooltip} from "react-native-paper";
import {BaseFilters} from "../../../../../web/hooks/useFilters";
import UpdateWorkOrderAssetModal from "./components/UpdateWorkOrderAssetModal";
import {TouchableOpacity, View} from "react-native";
import {MaterialIcons} from "@expo/vector-icons";
import {WorkOrderStatus} from "../../../../../../store/data/entities/work-order";
import {useSetLoading} from "../../../../../../hooks/useSetLoading";
import { humanizeDate, humanizeTime } from "../../../../../../utils/humanizers";
import useUser from "../../../../../../hooks/useUser";
import WorkOrderAssetOpenWorkOrdersModal from "../../../../../../components/modals/WorkOrderAssetOpenWorkOrdersModal";
import WorkOrderAssetFollowUpWorkOrdersModal from "../../../../../../components/modals/WorkOrderAssetFollowUpWorkOrdersModal";
import { PRIMARY_COLOUR } from "../../../../../../constants/GlobalStyle";
import { ImageViewer } from "../../../../../web-v2/components/ImageViewer";
import { useModal } from "../../../../../../hooks/useModal";
import WorkOrderAssetHistoryModal from "../../../../../../components/modals/WorkOrderAssetHistoryModal";

interface WorkOrderAssetsFilters extends BaseFilters {
  workOrderId: string;
  showChecked: boolean;
}

export default function WorkOrderAssetsScreen({route}: ScreenProps<WorkOrdersStackParamList, 'assets'>) {
  const workOrderId = route?.params?.id;
  const {data: workOrder, isFetching: workOrderFetching} = useGetWorkOrderQuery({
    id: workOrderId,
    includeAssets: false
  }, {
    skip: !workOrderId,
  });
  const [selectedWorkOrderAssetId, setSelectedWorkOrderAssetId] = React.useState<string | undefined>(undefined);
  
  

  const {user} = useUser();
  const canSeeInspected = user.role === 'Admin' || user.role === 'BuildingLead';
  const showHistoryButton = user.role !== 'Technician' &&  user.role !== 'ExternalTechnician';
  const canEdit =  user?.role !== 'ViewOnly' && ( workOrder?.status === WorkOrderStatus.Approved
    || workOrder?.status === WorkOrderStatus.Completed
    || workOrder?.status === WorkOrderStatus.InProgress);

  useSetLoading([workOrderFetching]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);


  const {showModal, hideModal} = useModal();
  const handleOnShowImagesModal = (images: string) => {
      showModal(
        <ImageViewer images={[images]} onClosed={hideModal}/>
      )
    }

  const {
    isLoading,
    filter,
    tableData
  } = useEntityListPage<WorkOrderAssetsFilters>({
    entityName: "Asset",
    lazyTableQuery: useLazyGetWorkOrderAssetsQuery,
    filterDefinition: {
      workOrderId,
      showChecked: true,
      pageNumber: 1,
      pageSize: 50,
      sortedColumn: '',
      sortedColumnAscending: false,
    },
  });



  const progress = React.useMemo(() => {
  const data = tableData?.list ?? [];
  if (data.length === 0) return "0/0";

  const completedCount = data.filter(item => item.isChecksCompleted).length;
  return `${completedCount}/${data.length}`;
}, [tableData]);

  return (
    <WebPageLayout header={GetWorkOrderScreenHeader(workOrderId)}>
      <View>
        <Row  style={{ marginBottom: 5, width: "100%" }}>
          <Col span={24}>
            <Card bordered style={{ width: "100%" }}>
              <Statistic
                title={
                  <span>
                    Progress
                  </span>
                }
                value={progress}
              />
            </Card>
          </Col>
        </Row>
      </View>
      <CoreTable<WorkOrderAsset>
        isLoading={isLoading}
        headers={[
          { text: 'Asset Code', sortedName: 'Asset.Code' },
          { text: 'Description', sortedName: 'Asset.Description' },
          { text: 'Supporting Information',sortedName: 'Asset.SupportingInformation' },
          ...(canSeeInspected ? [{ text: 'Inspected/Time Stamp',  sortedName: 'inspectedAt'}] : []),
          { text: 'Checked Completed', sortedName: 'isChecksCompleted' },
          { text: 'QR Tagged',  sortedName: 'isQRScanned'},
          { text: 'Follow-up Required',  sortedName: 'isFollowUpRequired' },
          { text: 'Log Book Entry',sortedName: 'comments'},
          { text: 'Images' },
          { text: 'Action' },
        ]}
        data={tableData}
        keyExtractor={(item) => item.id}
        renderItem={({item, index}) => {
          return (
            <CoreTable.Row style={{}} key={index}>
              <CoreTable.Cell >{item.assetCode}</CoreTable.Cell>
              <CoreTable.Cell >{item.assetDescription}</CoreTable.Cell>
              <CoreTable.Cell >{item.supportingInformation}</CoreTable.Cell>
              {canSeeInspected && (
                <CoreTable.Cell >
                  {humanizeTime(item.inspectedAt)} {humanizeDate(item.inspectedAt)}
                </CoreTable.Cell>
              )}
              <CoreTable.Cell >{item.isChecksCompleted ? 'Y' : 'N'}</CoreTable.Cell>
              <CoreTable.Cell >{item.isQRScanned ? 'Y' : 'N'}</CoreTable.Cell>
              <CoreTable.Cell >
                {item.isFollowUpRequired ? 'Y' : 'N'}
                {item.isFollowUpRequired && (
                  <WorkOrderAssetFollowUpWorkOrdersModal
                    workOrderId={item.followUpWorkOrderId}
                    triggerContent={<Button type="link" size="small">View</Button>}
                  />
                )}
                
              </CoreTable.Cell>
              <CoreTable.Cell >{item.comments}</CoreTable.Cell>
              <CoreTable.Cell >
                {item.imageUri != null &&
                  <TouchableOpacity onPress={() => handleOnShowImagesModal(item.imageUri)}>
                    <MaterialIcons name={"photo" } size={24}
                                    color={PRIMARY_COLOUR}/>
                  </TouchableOpacity> 
                }
              </CoreTable.Cell>
              <CoreTable.Cell >
                {canEdit && (
                  <TouchableOpacity onPress={() => { setUpdateModalOpen(true); setSelectedWorkOrderAssetId(item.id); }}>
                    <MaterialIcons name="edit" size={24} color="black" />
                  </TouchableOpacity>
                )}
                {showHistoryButton && (
                  <Tooltip title="View all previous updates">
                    <TouchableOpacity onPress={() => { setHistoryModalOpen(true); setSelectedWorkOrderAssetId(item.id); }} style={{ marginRight: 4 }}>
                      <MaterialIcons name="history" size={24} color="black" />
                    </TouchableOpacity>
                  </Tooltip>
                )}
              </CoreTable.Cell>

            </CoreTable.Row>
          )
        }}
        filters={filter.filters}
        setFilter={filter.setFilter}
      >
      </CoreTable>

      <UpdateWorkOrderAssetModal
        workOrderAssetId={selectedWorkOrderAssetId}
        workOrderId={workOrderId}
        open={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false);
          setSelectedWorkOrderAssetId(undefined);
        }}
      />
      <WorkOrderAssetHistoryModal
        workOrderAssetId={selectedWorkOrderAssetId}
        isOpen={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false);
          setSelectedWorkOrderAssetId(undefined);
        }}
      />
    </WebPageLayout>
  )
}
